import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { NeuralCanvas, useStageSize } from './canvas/NeuralCanvas'
import { useFieldInset } from './canvas/fieldInset'
import { SpatialCanvas } from './canvas/SpatialCanvas'
import { Labels } from './canvas/Labels'
import { ToolDock } from './ui/CanvasChrome'
import { ExplorationsSidebar, RAIL_W } from './ui/ExplorationsSidebar'
import { SIDEBAR_INSET_VAR, rightReserve, sidebarWidthFrom } from './ui/sidebarPane'
import { Composer } from './ui/Composer'
import { Thread } from './ui/Thread'
import { THREAD_TAB_W, THREAD_W_VAR, threadWidthFrom } from './ui/threadPane'
import { Minimap, Thoughtstream } from './ui/Overlay'
import { GraphFooter } from './ui/Ledger'
import { FieldCard } from './ui/FieldCard'
import { CommandPalette } from './ui/CommandPalette'
import { RunStrip } from './ui/RunStrip'
import { RunDoneBar } from './ui/RunDoneBar'
import { ProgrammeSheet } from './ui/Programme'
import { CoachDirector, CoachPopup, cycleCoachLens, skipCue, takeCueAction } from './ui/Coach'
import { CoachPins } from './ui/CoachPins'
import { coachGateOpen, cueAuditor, cueRefusal, liveCoach, visibleCues } from './ui/coachView'
import { CUE_PRICE_VERB } from './core/cues'
import { Toasts } from './ui/Dialogs'
import { SetupSheet } from './ui/Setup'
import { SourcesDialog } from './ui/Sources'
import { Library } from './ui/Library'
import { ExplorationSearch } from './ui/ExplorationSearch'
import { ShortcutsSheet } from './ui/Shortcuts'
import { PromptSheet } from './ui/PromptSheet'
import { Icon } from './ui/components/Icon'
import { canCall, resolveFor, useStore } from './core/store'
import type { UpNextVerb } from './core/upNext'
import { fmtUsd, type ModelEntry } from './core/models'
import { projectVerbUsd } from './engine/run'
import { initAnalytics, markSession } from './core/analytics'
import { collectOrphanConversations } from './core/conversations'
import { armNarrator } from './ui/narrator'
import { armUpNext, pressVerb } from './ui/upNextPress'
import { UpNext } from './ui/UpNext'
import { letterKeyMayAct } from './ui/letterKeys'
import { checkPressOf } from './ui/paletteVerbs'
import { stopRun } from './engine/run'
import { loadDemo } from './engine/demo'
import { useMedia } from './ui/components/useMedia'
import { useAdjustOpen } from './ui/adjust'
import {
  addressAfterLink,
  carriesExplorationLink,
  linkCopy,
  readSharedExploration,
  type SharedExplorationRead,
} from './core/share'
import { SharedCopyBanner, readFirstOf, useReadFirst } from './ui/SharedCopyBanner'
import { sanitizeResearchState } from './core/research'
import { canonicalSemanticJson } from './core/semanticGit'

// The two heaviest sheets are loaded when they are first opened, not at boot.
//
// Imported statically and mounted unconditionally — each returning `null` until
// its `open` flag was set — they still put their modules and everything only
// they import on the boot path: the Research Lab's 96 KB and its own CSS, the
// export sheet with the PDF report, the audit capsule and the standalone
// verifier's source. Measured, that was 448 KB of the 1,613 KB entry chunk
// (113 KB gzipped) for a first-run visitor who opens neither
// (docs/performance-audit.md, finding 3). Both are reached through `lazy`; the
// Lab stays mounted after first opening so close/reopen preserves its workspace.
// A stale deploy that 404s a chunk is the preload-error case `boot.tsx` reloads on.
const ExportSheet = lazy(() => import('./ui/Export').then((m) => ({ default: m.ExportSheet })))
const ResearchLab = lazy(() => import('./ui/ResearchLab').then((m) => ({ default: m.ResearchLab })))

/** Module-level, not a ref: StrictMode remounts must not re-trigger the load. */
let bootstrapped = false

/** The link leaves the address — its fragment, and any routing a link made before
 *  D332 carried with it, so the address is the app's mount (`addressAfterLink`). */
function clearShareFragment() {
  window.history.replaceState(
    window.history.state,
    '',
    addressAfterLink(window.location.href, import.meta.env.BASE_URL),
  )
}

/** Install or report one already-decoded share. Returns true only on a restore. */
function applySharedExploration(shared: SharedExplorationRead): boolean {
  const s = useStore.getState()
  if (shared.kind === 'ok') {
    // Read-first: the copy opens on the canvas and its Overview (`restoreSession`
    // turns the pane to it, holding the reader's own tab aside), with one line in
    // place of the composer until the reader presses Continue working
    // (`ui/SharedCopyBanner.tsx`). The one door that marks a copy so. On a phone
    // the Overview is the sheet, so the sheet comes up with it.
    const copy = linkCopy(shared.doc)
    s.restoreSession(copy)
    if (window.matchMedia('(max-width: 767px)').matches) s.setRail(true)
    const seed = copy.session.seed || 'Untitled exploration'
    s.toast(`Opened a shared copy of “${seed.slice(0, 80)}${seed.length > 80 ? '…' : ''}”.`, 'good')
    return true
  }
  if (shared.kind === 'too-large') {
    s.toast('That shared exploration is too large for this browser to open.', 'bad')
  } else if (shared.kind === 'unsupported') {
    s.toast('This browser cannot open that exploration link format.', 'bad')
  } else if (shared.kind === 'invalid') {
    s.toast('That exploration link is incomplete or invalid.', 'bad')
  }
  return false
}

export default function App() {
  const stageRef = useRef<HTMLElement | null>(null)
  const researchVerificationRequest = useRef(0)
  const stage = useStageSize(stageRef)
  // The bottom band — the floating composer, or the phone's sheet — measured into
  // `viewInset.bottom` off the stage, whichever view is showing. The flat camera
  // frames above it and the toasts stand above it (`ui/Dialogs.tsx`) in both.
  useFieldInset(stageRef)

  const railRight = useStore((s) => s.railRight)
  /** The width the reader dragged the thread column to, or null for the default.
   *  Read raw and clamped below: the bounds are a fact about this window. */
  const storedThreadW = useStore((s) => s.threadWidth)
  /** …and the explorations sidebar, the same way (D104) — with the width its
   *  names need, which the pane measures and which caps how wide it may go. */
  const storedSidebarW = useStore((s) => s.sidebarWidth)
  const sidebarFit = useStore((s) => s.sidebarFit)
  const sidebarCollapsed = useStore((s) => s.sidebarCollapsed)
  const selectedId = useStore((s) => s.selectedId)
  const rightTab = useStore((s) => s.rightTab)
  const setRail = useStore((s) => s.setRail)
  const nodeCount = useStore((s) => s.session.order.length)
  const theme = useStore((s) => s.theme)
  // A finished card is not a live run: the trace has stopped, so it goes back to
  // the centre while the card is still on screen waiting to be read.
  const runLive = useStore((s) => s.run !== null && s.run.phase !== 'ended')
  const viewMode = useStore((s) => s.viewMode)
  const showMinimap = useStore((s) => s.showMinimap)
  const sessionId = useStore((s) => s.session.id)
  const researchUpdatedAt = useStore((s) => s.session.research?.updatedAt ?? null)
  // The gates for the two lazy sheets above. Each sheet reads its own flag as
  // well; these decide whether the sheet is MOUNTED, which is what keeps its
  // module off the boot path.
  const researchOpen = useStore((s) => s.researchOpen)
  const exportOpen = useStore((s) => s.exportOpen)
  const [researchLoaded, setResearchLoaded] = useState(false)
  useEffect(() => {
    if (researchOpen) setResearchLoaded(true)
  }, [researchOpen])
  // Phone width: the thread becomes a bottom sheet instead of a side column,
  // so it stops insetting the canvas and the composer yields to it. One query,
  // read here and in the thread, so the two agree on where the seam is.
  const narrow = useMedia('(max-width: 767px)')
  // The phone folds the replay dock behind the composer's Adjust press, with
  // the four dials (`ui/adjust.ts`) — except while a replay is under way, when
  // the transport is live state and its skip, the way back, may not be hidden.
  const adjust = useAdjustOpen()
  // A copy opened from a link and not yet continued: the composer stands down
  // for the one line that says whose it is (`ui/SharedCopyBanner.tsx`).
  const readFirst = useReadFirst()

  // Keep the DOM attribute in step with the store, so a theme set before React
  // mounted (the inline script in index.html) and one set after agree.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  // First run lands on something worth looking at rather than an empty canvas.
  //
  // A shared exploration gets first refusal. Its fragment is removed after this
  // one read, both so a reload opens the local copy rather than importing another
  // one and so a copied address never accidentally re-shares somebody else's
  // graph. Without one, the demo starts FIRST and the splash opens over it, so the
  // dimmed backdrop behind the sheet is a real exploration unfurling.
  useEffect(() => {
    if (bootstrapped) return
    bootstrapped = true
    void (async () => {
      const shared = await readSharedExploration(window.location.hash)
      const s = useStore.getState()

      if (shared.kind !== 'none') {
        // This app has no fragment router. Remove the payload without a reload;
        // keeping a megabyte of graph in the omnibox after it has become local
        // state serves no purpose and makes an accidental second share too easy.
        clearShareFragment()
      }
      // Both no-ops unless this deploy set VITE_ANALYTICS_DOMAIN, which no clone
      // does — see `core/analytics.ts`. AFTER a share fragment is gone, still: no
      // vendor script runs here since D343, and the beacon builds its url from
      // origin and path alone, but the address a pageview describes should be the
      // one the reader is left on, not the link that brought them.
      initAnalytics()
      markSession()
      // Transcripts whose exploration is gone. The two Forget doors take their
      // own conversations directly; this is the backstop for what those cannot
      // see — a second tab clearing the library, an exploration removed before
      // conversations were ever kept. A scan of
      // the origin's own keys, once, at the moment the app is already booting.
      void collectOrphanConversations()
      // The voice, wired to the store from here rather than by a module-load
      // side effect: it spends, so its arming is a line somebody can point to.
      armNarrator()
      // And the Up next line, for the same reason: it runs a press the reader
      // made earlier, when the pass in front of it lands — a spend, so its
      // arming is a line too. `ui/upNextPress.ts` has the guardrail.
      armUpNext()

      const openedShared = applySharedExploration(shared)

      // A failed read is an unknown investigation, not a new empty workspace.
      if (!openedShared && s.session.order.length === 0 && s.saveState !== 'failed') void loadDemo()
      // On a phone the thread is a bottom sheet; opening it on load buries the
      // graph AND the composer behind it. Start it closed there — a node tap
      // reopens it — regardless of the persisted (desktop) rail preference.
      // Except over a copy read first, which opens on its Overview: its composer
      // has stood down, and the sheet is where a phone reads.
      if (window.matchMedia('(max-width: 767px)').matches)
        s.setRail(readFirstOf(useStore.getState()))
      // A share link is already an invitation to look around, so it does not put
      // the first-run setup sheet over the graph. It also does not mark that sheet
      // as seen; the normal first-run door remains intact for the next visit.
      if (!openedShared && !s.splashSeen && !canCall(s) && s.setupOpen === null)
        s.setSetup('splash')
    })()
  }, [])

  // Persistence has to decode synchronously during store construction, while
  // commit hashes and critique signatures require Web Crypto. Authenticate the
  // retained research records as soon as each exploration becomes current and
  // replace its structural startup copy only if it is still the same copy. This
  // makes a restore/import race fail closed instead of applying an older audit
  // result over a newer local edit.
  useEffect(() => {
    const request = ++researchVerificationRequest.current
    const candidate = useStore.getState().session.research
    if (!candidate) return
    let live = true
    void (async () => {
      const verified = await sanitizeResearchState(candidate, {
        fallbackId: candidate.id,
        now: candidate.createdAt,
      })
      if (!live || request !== researchVerificationRequest.current) return
      const store = useStore.getState()
      if (store.session.id !== sessionId || store.session.research !== candidate) return
      // Verification that retains the exact same bytes is not a semantic edit.
      // Writing it through `setResearchState` would append/coalesce a knowledge
      // revision and move the latest "as known" timestamp merely by opening the app.
      if (canonicalSemanticJson(candidate) !== canonicalSemanticJson(verified.state)) {
        store.setResearchState(verified.state)
      }
      const quarantined = verified.diagnostics.filter(
        (row) => row.code === 'semantic-log-rejected' || row.code === 'critique-rejected',
      ).length
      if (quarantined) {
        store.toast(
          `${quarantined} semantic history record${quarantined === 1 ? ' was' : 's were'} quarantined because its cryptographic proof did not verify.`,
          'warn',
        )
      }
    })()
    return () => {
      live = false
    }
  }, [researchUpdatedAt, sessionId])

  // A full load is not guaranteed: pasting a link into an already-open tab on
  // the same origin is a fragment navigation, so React and the module-level boot
  // latch stay alive. Listen for that door explicitly or the address changes
  // while the graph does not, which looks exactly like a link to the wrong
  // exploration.
  useEffect(() => {
    let live = true
    let request = 0
    const onHashChange = () => {
      const hash = window.location.hash
      if (!carriesExplorationLink(hash)) return
      // Synchronous and before decompression: graph content leaves the address in
      // our first turn through the event rather than after an awaited stream read,
      // so nothing that reads the address meanwhile ever sees it. That was the
      // analytics script until D343; it is still any extension reading the page.
      clearShareFragment()
      const ticket = ++request
      void (async () => {
        const shared = await readSharedExploration(hash)
        if (!live || ticket !== request || shared.kind === 'none') return
        const s = useStore.getState()
        if (shared.kind === 'ok' && s.busy) {
          s.toast('A pass is still running. Stop it before opening a shared exploration.', 'warn')
          return
        }
        applySharedExploration(shared)
      })()
    }
    window.addEventListener('hashchange', onHashChange)
    return () => {
      live = false
      window.removeEventListener('hashchange', onHashChange)
    }
  }, [])

  // Camera follow lives in the canvas render loop — see NeuralCanvas.

  /* ── Keyboard ───────────────────────────────────────────────────────── */

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement
      const typing =
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        (el instanceof HTMLElement && el.isContentEditable)

      const s = useStore.getState()
      const mod = e.metaKey || e.ctrlKey
      const key = e.key.toLowerCase()
      // The coach every binding below reads, through the one helper the card,
      // the bar and the pins read it through: a keystroke must never act on a
      // guide that belongs to a session other than the one on screen.
      const coach = liveCoach(s)

      if (mod && key === 'k') {
        e.preventDefault()
        // Never a SECOND modal — the `?` rule below, for the same reason: the
        // sheets share one `inert` flag, and the palette's cleanup would clear
        // it under whichever sheet is still up, handing the canvas back to the
        // tab order behind a live modal. Closing the palette is always allowed;
        // opening it over a sheet waits for Escape to clear the sheet first.
        if (
          !s.paletteOpen &&
          (s.shortcutsOpen ||
            s.promptOpen ||
            s.setupOpen !== null ||
            s.sourcesOpen ||
            s.researchOpen ||
            s.libraryOpen ||
            s.searchOpen ||
            s.exportOpen ||
            s.tuneOpen)
        )
          return
        s.setPalette(!s.paletteOpen)
        return
      }
      // The keyboard reference, on the key it is named after. Up here beside ⌘K
      // and not down with the verbs, because the guard below returns for any open
      // sheet — including this one, whose key must still close it. `e.key === '?'`
      // and not a code: `key` is the produced character, so it is layout-correct
      // wherever `?` is reachable.
      // Every other sheet is named here, and this key must not open a SECOND
      // modal over one of them: `useModalFocus` sets `main.stage.inert` on a
      // single flag, so the second sheet's cleanup would clear inert while the
      // first is still up — handing the whole canvas back to the tab order
      // behind a live modal. Its own flag is absent from the list on purpose,
      // so `?` still closes the reference it opened.
      if (
        !typing &&
        !mod &&
        e.key === '?' &&
        !s.paletteOpen &&
        s.setupOpen === null &&
        !s.sourcesOpen &&
        !s.researchOpen &&
        !s.libraryOpen &&
        !s.searchOpen &&
        !s.exportOpen &&
        !s.promptOpen &&
        !s.tuneOpen
      ) {
        e.preventDefault()
        s.setShortcuts(!s.shortcutsOpen)
        return
      }
      // New exploration. Browsers reserve ⌘N for a new window and some will not
      // yield it; the sidebar's button is the path of record and this is the
      // bonus.
      if (mod && key === 'n' && !e.shiftKey) {
        e.preventDefault()
        // The shared door — one lock for the sidebar's button, the palette's row
        // and this key, so the three cannot drift apart again.
        s.newExploration()
        return
      }
      if (mod && e.key === ']') {
        e.preventDefault()
        s.setRail(!s.railRight)
        return
      }
      // The other half of the pair, bound again. D18 retired ⌘[ with the library
      // rail and Shortcuts.tsx recorded it as deliberately unbound; there is a
      // left pane once more, and the two brackets mean the two panes.
      //
      // Above the phone breakpoint only. Below it the sidebar is an overlay with
      // its own opener and its own live-run refusal, and a chord for a device
      // with no keyboard would be a binding nobody can press. Read from
      // matchMedia rather than a subscription for the reason this whole handler
      // is: it is installed once and reads the store at press time. Where it is
      // not ours, the key is LEFT ALONE — see below.
      if (mod && e.key === '[') {
        // The width is asked BEFORE the key is taken, not after. Below the
        // breakpoint this chord is not ours, and a `preventDefault` up here
        // swallowed it anyway — so a desktop window dragged narrow lost the
        // browser's own Back to a binding that then did nothing at all.
        if (!window.matchMedia('(max-width: 767px)').matches) {
          e.preventDefault()
          s.setSidebarCollapsed(!s.sidebarCollapsed)
        }
        return
      }

      // Inside the mod block, because the `typing || mod` return below swallows
      // every modified key. Unlike ⌘K this gates on `typing` itself: in a field
      // ⌘Z is the browser's own text undo, and stealing it would break the seed
      // box, the rewrite editor, the key field and the palette input.
      if (mod && (key === 'z' || key === 'y')) {
        // Focus moves to a palette row the moment one is hovered, after which
        // `typing` is false and this would fire behind the modal — so both
        // layers are named, the way the Escape ladder below already names them.
        if (
          typing ||
          s.paletteOpen ||
          s.shortcutsOpen ||
          s.promptOpen ||
          s.setupOpen !== null ||
          s.sourcesOpen ||
          s.researchOpen ||
          s.libraryOpen ||
          s.searchOpen ||
          s.exportOpen ||
          s.tuneOpen
        )
          return
        e.preventDefault()
        if (s.busy) {
          // Deliberately not an abort: cancelling a paid, partly-delivered call
          // on a reflex keystroke is destructive and is itself not undoable.
          // Stop is on the composer.
          s.toast('One thought at a time — let the current branch finish.', 'warn')
          return
        }
        if (key === 'y' || e.shiftKey) s.redo()
        else s.undo()
        return
      }

      if (e.key === 'Escape') {
        if (s.paletteOpen) s.setPalette(false)
        else if (s.shortcutsOpen) s.setShortcuts(false)
        else if (s.promptOpen) s.setPromptOpen(false)
        else if (s.exportOpen) s.setExport(false)
        else if (s.libraryOpen) s.setLibrary(false)
        else if (s.searchOpen) s.setSearchOpen(false)
        else if (s.researchOpen) s.setResearchOpen(false)
        else if (s.sourcesOpen) s.setSources(false)
        else if (s.setupOpen !== null) s.setSetup(null)
        else if (s.tuneOpen) s.setTune(false)
        // The coach's card is the topmost thing a reader has put down, so it
        // goes first, then the finished run's head on the run strip, then the
        // pins the coach raised over the graph (`bar`, named for the bar that
        // used to report the run) — three rungs because they are three
        // separate things you can be done with, and one press each. All of
        // them go before a focus you may still be using. A LIVE run is
        // deliberately not on this ladder: Escape must never be the thing that
        // stops something costing money.
        // `liveCoach`, not `s.coach`: the rungs below are about surfaces on
        // screen, and a coach belonging to another session puts none there — a
        // rung that swallowed Escape for it would strand the focus below it.
        else if (coach?.open || coach?.digest) s.dismissCoach()
        else if (s.run?.phase === 'ended') s.setRun(null)
        else if (coach?.bar) s.closeCoachBar()
        else if (s.focusId) s.setFocus(null)
        else s.select(null)
        return
      }

      if (typing || mod) return
      // The third guard, and it is not decoration. The old key dialog held only an
      // input, so `typing` covered it by accident; a sheet holding focusable model
      // rows does not — and a bare `c` would Challenge a node behind the splash in
      // someone's first ten seconds.
      if (
        s.paletteOpen ||
        s.shortcutsOpen ||
        s.promptOpen ||
        s.setupOpen !== null ||
        s.sourcesOpen ||
        s.researchOpen ||
        s.libraryOpen ||
        s.searchOpen ||
        s.exportOpen ||
        s.tuneOpen
      )
        return
      // The fourth guard, for every bare letter below — verbs, coach keys and
      // view keys alike. `typing` covers a field; a focused BUTTON is not one,
      // and a person typing a question at a button in the thread fired a paid
      // Deepen on its first "d". A letter is the app's only where focus is on
      // nothing in particular, or on a surface built for the letters (the
      // graph's captions, the node list, the pins, the coach's card). See
      // `letterKeyMayAct`, which the test pins over every kind of control.
      if (/^[a-z]$/.test(key) && !letterKeyMayAct(el)) return

      // ── The coach ────────────────────────────────────────────────────────
      // G is the door and is bound whether or not the card is up. Everything
      // else here is bound only while the card is SHOWING and only at rest —
      // the very gate the card renders behind, read from the one helper every
      // coach surface reads, so a key can never act a cue that is not on
      // screen or a cue in a different position than the card shows.
      //
      // Deliberately not added to the open-sheet guard above: the coach is not
      // a modal, it is a card floating beside a node on the graph, and the verbs
      // must keep working over it.
      //
      // The typing guard's missing case, and it belongs to this block alone
      // because this block is what added the keys that collide. A <select> is
      // not "typing" but it answers to ↑/↓, Enter and letter typeahead itself,
      // and the ledger dock's Speed and Pace pickers are always-on chrome
      // focusable at exactly the playhead position the coach binds at — so a
      // press meant for a dropdown would step the list, or take a cue and spend
      // on it. The control the reader is actually operating wins.
      // The keyboard's parity with the buttons, for anything that spends. The
      // buttons show the figure on hover — a glance before the press — and the
      // keyboard has no hover, so a keyed paid pass toasts the same
      // projectVerbUsd arithmetic at fire time: same leans-high wording, no
      // confirmation friction (the buttons have none either — the cap, not a
      // dialog, is what bounds a run). Only when the pass can actually run:
      // pricing a keyless refusal would put a figure on nothing — asked of the
      // route the verb runs on, never the selection's key, which priced a
      // routed pass that then refused (invariant 4, D286). Declared
      // before the coach block because Enter on a paid cue is one of its
      // callers; the verb keys below are the others.
      const priced = (
        name: string,
        verb: 'move' | 'verify' | 'priorArt' | 'panel' | 'audit',
        fire: () => void,
        auditor: ModelEntry | null = null,
      ) => {
        const routed =
          verb === 'audit'
            ? auditor !== null
            : resolveFor(s, verb === 'move' ? 'reason' : verb === 'panel' ? 'panel' : 'check') !==
              null
        if (routed) {
          const est = projectVerbUsd(
            { modelId: s.modelId, roleModels: s.roleModels },
            verb,
            auditor,
          )
          if (est !== null)
            s.toast(`${name} — up to about ${fmtUsd(est)}, an estimate that leans high.`, 'info')
        }
        fire()
      }

      const onControl = el instanceof HTMLSelectElement
      if (key === 'g' && !onControl) {
        e.preventDefault()
        // Auto-repeat is not a second press. A held key fires tens of times a
        // second, and every binding in this block that RETIRES a cue, SPENDS on
        // one, or toggles a surface would then run against a board that changed
        // under it between repeats — a held Enter would act the next cue the
        // retirement just surfaced, which nobody chose. Walking the list (J/K)
        // and reading (W/O) are safe to repeat and deliberately still do.
        if (e.repeat) return
        // A false return refuses for the same reason the dock's spark does, and
        // the dock is where that sentence belongs; a key that quietly does
        // nothing is the honest answer at the keyboard.
        if (coach?.open) s.dismissCoach()
        else s.openCoach()
        return
      }
      // L is bound with the card CLOSED as well as open — the one key here that
      // is, and for the reason the digest exists: it REPLACES the pins, so the
      // reader who never opened a card is exactly the reader who wants the list.
      // Everything else in this block is about the cue the card is showing, and
      // there is no such cue when the card is down.
      if (key === 'l' && coach && coachGateOpen(s) && !onControl) {
        e.preventDefault()
        if (e.repeat) return
        s.toggleCoachDigest()
        return
      }
      if (coach?.open && coachGateOpen(s) && !onControl) {
        const cues = visibleCues(s)
        if (cues.length > 0) {
          const index = Math.min(Math.max(0, coach.index), cues.length - 1)
          const cue = cues[index]!
          // Wrapping, because the list is short and its ends are not walls: J at
          // the bottom of eight cues means "the next one", and stopping dead
          // there reads as a broken key rather than as an edge.
          const step = (d: number) => {
            e.preventDefault()
            s.setCoachIndex((index + d + cues.length) % cues.length)
          }
          // The arrows are ALIASES, and only where they are free: the spatial
          // view binds ↑/↓ to the orbit in its own handler, and one press
          // turning the camera AND changing the cue is the ambiguity this app
          // declined when it refused to make one letter mean two things. J and
          // K are unambiguous and work in both views.
          const arrows = s.viewMode !== '3d'
          if (key === 'j' || (arrows && key === 'arrowdown')) return step(1)
          if (key === 'k' || (arrows && key === 'arrowup')) return step(-1)
          // Not when a button holds focus: the browser is already about to
          // press it, and acting here as well would take the cue twice.
          if (
            key === 'enter' &&
            !(el instanceof HTMLButtonElement) &&
            !(el instanceof HTMLAnchorElement)
          ) {
            e.preventDefault()
            if (e.repeat) return
            // The card shows the figure only on hover, and Enter has no hover —
            // so a keyed paid press goes through `priced`, the verb keys'
            // parity, with the audit's figure at its auditor's own rates. The
            // link scan has no projection here; its charge arrives in the
            // pass's own ending sentence.
            //
            // Never over a refusal, though: `takeCueAction` will decline this
            // press and say why, and a figure printed first would put a price on
            // a pass that cannot run — the same reason `priced` asks the route
            // before it quotes anything.
            const pverb = cue.action ? CUE_PRICE_VERB[cue.action.kind] : null
            if (pverb && !cueRefusal(s, cue)) {
              const auditor = pverb === 'audit' ? (cueAuditor(s, cue)?.entry ?? null) : null
              priced(cue.action!.label, pverb, () => takeCueAction(cue), auditor)
            } else {
              takeCueAction(cue)
            }
            return
          }
          if (key === 's') {
            e.preventDefault()
            if (e.repeat) return
            // The card's Skip, through the one function both presses share.
            skipCue(cue)
            return
          }
          if (key === 'w') {
            e.preventDefault()
            s.toggleCoachWhy()
            return
          }
          if (key === 'o') {
            e.preventDefault()
            cycleCoachLens()
            return
          }
        }
      }

      // Single-key verbs act on the selection. Cheap to reach, and the
      // inspector shows the same letters so they are discoverable.
      //
      // Every paid verb goes through `pressVerb`, the path the node's own
      // buttons take: with the lock free the verb runs now (the six moves ask
      // `lockHeld`, so the worked example's unfurl gives way to them, D122);
      // with it held the press is queued behind the pass in flight — the Up
      // next line — or refused out loud, in the button's own sentence. It used
      // to be dropped here without a word, and a person who pressed T, I and B
      // saw only T run. The keyed price toast is `pressVerb`'s too, so a
      // queued key quotes its figure exactly as a key that fires at once does.
      const sel = s.selectedId
      const press = (verb: UpNextVerb) => {
        // Auto-repeat is not a second press. With a queue behind the lock, a
        // held D would put two paid passes up next before the finger lifts.
        if (!sel || e.repeat) return
        pressVerb(verb, sel, { keyed: true })
      }
      // The two FREE forms open writing surfaces rather than run passes, so the
      // lock has nothing to say about them: recording never takes it, and the
      // rewrite's one paid press waits on the Up next line like any other
      // (`pressRewrite`). They used to refuse under the lock, which left the
      // card's own Rewrite link the only door open to a box that costs nothing.
      const form = (open: () => void) => {
        if (sel) open()
      }
      const verbs: Record<string, () => void> = {
        d: () => press('deepen'),
        c: () => press('challenge'),
        x: () => press('diverge'),
        t: () => press('experiment'),
        i: () => press('invert'),
        b: () => press('expand'),
        // `u` for what is unstated. Not `n`: a key reading as "new" would say
        // the one thing the pass is forbidden to claim about what it proposes.
        // A verb the Up next line holds, as the card's button is (D286): its
        // price is quoted off the reasoning route's key, never the selection's,
        // and under the lock it waits rather than refusing in a bare sentence.
        u: () => press('conjecture'),
        // `r` and `e` — bindable now that the inspector's FREE rows wear those
        // exact chips, so the keystroke is labelled before it is ever pressed.
        r: () => form(() => s.pokeRecord()),
        e: () => form(() => s.pokeRewrite()),
        // The two checks: `v` for verify and `p` for panel. On the seed, `v` is
        // the survey of the field — the check's pass and price under its own name.
        // On a conjecture it is the prior-art search, the one search a conjecture
        // gets (D281, D288), under its own verb — which the Up next line holds, as
        // the card's button is (D286), so under the lock it waits like V anywhere.
        v: () => {
          const on = sel ? s.session.nodes[sel] : undefined
          press(on ? checkPressOf(on) : 'verify')
        },
        p: () => press('panel'),
        // Not gated on `busy`, alone among these. Asking takes no lock, adds no
        // node and takes no step of history, so there is nothing for it to race —
        // and the whole point of the channel is that it works while something else
        // is running.
        a: () => sel && s.askAbout({ kind: 'node', id: sel }),
      }
      if (verbs[key]) {
        e.preventDefault()
        s.dismissCoach()
        verbs[key]!()
        return
      }
      if (key === 'f') {
        e.preventDefault()
        s.setFocus(null)
        const r = stageRef.current?.getBoundingClientRect()
        if (r) s.frame(r.width, r.height)
      }
      // `h` for hide. NOT `z`: ⌘Z is undo, and a bare Z sitting next to a
      // "⌘Z to put it back" toast in the same panel is one letter too clever.
      if (key === 'h') {
        e.preventDefault()
        s.dismissCoach()
        if (sel) s.toggleCollapse(sel)
      }
      // The view toggle, on a digit because every letter that fits is spoken for
      // — `v` is verify, and a verb that spends money has the stronger claim on
      // its own initial. It sat on `v` once, unreachably: the verbs map above
      // returns for any key it holds, so the letter never arrived here and a
      // keystroke the README sold as free navigation ran a paid search instead.
      if (key === '3') {
        e.preventDefault()
        const r = stageRef.current?.getBoundingClientRect()
        s.setViewMode(s.viewMode === '3d' ? '2d' : '3d', r?.width, r?.height)
      }
      if (key === ' ') {
        e.preventDefault()
        s.dismissCoach()
        // Stop takes precedence over the replay toggle while a run is live. The
        // reflex a person reaches for to halt something moving is the space bar,
        // and toggling a playhead instead would hide everything the run had
        // produced behind a scrub position the run does not clear.
        if (s.run && s.run.phase !== 'ended') stopRun()
        else if (s.playing) s.setPlaying(false)
        else {
          // Same start as the dock's play button: from the top when at live,
          // else from where the playhead already stands.
          if (s.playhead === null || s.playhead >= s.session.order.length) s.setPlayhead(1)
          s.setFocus(null)
          s.setPlaying(true)
        }
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // On a phone the thread is a bottom sheet over the graph and the composer,
  // so an EMPTY one is pure loss: a tap on bare canvas deselects, and what is
  // left covering the field is a panel with nothing anchored in it. The tap
  // that opened it, a node tap, reopens it.
  //
  // On the TRANSITION out of a selection, not on the state: the Breakthroughs,
  // Ask and Run tabs all hold content with nothing selected, and reacting to the
  // state would also close the sheet under the finger of someone who had just
  // switched to the Node tab. Every deselect route is covered at once — canvas
  // tap, spatial tap, and the Escape ladder's `select(null)` above.
  const hadSelection = useRef(selectedId)
  useEffect(() => {
    const lost = hadSelection.current !== null && selectedId === null
    hadSelection.current = selectedId
    if (lost && narrow && railRight && rightTab === 'node') setRail(false)
  }, [selectedId, narrow, railRight, rightTab, setRail])

  // …and the sheet rising over a replay that is PLAYING — a node tapped, a
  // shared copy's Overview pressed — takes it to the finished graph. The
  // transport, and the interval that moves it, live in the strip the sheet
  // displaces, so the replay used to stop where it stood: half a graph above the
  // sheet, the loop held at full rate for it, and the way back hidden with the strip.
  // A replay the reader paused is a place they chose, and stays.
  useEffect(() => {
    if (!narrow || !railRight) return
    const s = useStore.getState()
    if (!s.playing) return
    s.setPlaying(false)
    s.setPlayhead(null)
  }, [narrow, railRight])

  /**
   * All the panes float OVER the canvas — the stage runs the full width and
   * their glass blurs live strands, exactly like the capsule and composer. The
   * canvas a reader can actually see is the FIELD right of the thread column,
   * and everything that must stay readable lives there: the floating chrome
   * strips, the empty-state hero, and — via `viewInset` — the camera's own idea
   * of where the visible canvas begins and ends.
   */
  // The explorations sidebar's column, and the one number every other piece of
  // chrome on that side is measured from. Below the breakpoint the sidebar is an
  // overlay with its own opener rather than a column — it reserves nothing, the
  // mirror of how the thread reserves nothing there — so this is 0 and the
  // corner keeps the plain 16 it has always had.
  //
  // Its expanded width is a number the reader can drag (D104), read from the
  // store and held inside what this window allows — the thread's own floor,
  // or its tab, plus the field's — so the two panes and the field are one
  // arithmetic: the sidebar is clamped first against what the thread must
  // keep, then the thread against what the sidebar took.
  const threadOpen = railRight && !narrow && nodeCount > 0
  /**
   * The tab that brings the column back, and the column's reserve when it is
   * away.
   *
   * D18 deleted a reopen tab and was right to: that one led to a RAIL with no
   * jobs left in it, so the tab was a control for a place worth nothing. This
   * pane holds the conversation — the chat, the reading, the composer — so a
   * closed column is a closed door rather than a tidied surface, and the way
   * back has to be visible on the field. ⌘] still works and always did; a key
   * nobody can see is not a door for the reader who closed the pane by mistake.
   *
   * It reserves its own 13px from the field for the reason `THREAD_TAB_W` gives:
   * the camera's inset is what keeps nodes and captions out from under chrome,
   * so a tab outside it would be a control drawn over the graph.
   */
  const threadTab = !railRight && !narrow && nodeCount > 0
  const threadReserve = rightReserve(threadOpen, threadTab)
  const sidebarW = sidebarWidthFrom(stage.w, threadReserve, storedSidebarW, sidebarFit)
  const sidebarInset = narrow ? 0 : sidebarCollapsed ? RAIL_W : sidebarW
  // The thread column, beside the sidebar. On a phone it is a bottom sheet and
  // reserves nothing; on an empty canvas it is not mounted at all, so the seed
  // moment keeps the whole field. Its width is a clamped share of the stage —
  // see `threadWidth` — computed HERE so the pane's box and the camera's inset
  // are one number that cannot drift apart.
  const threadW = threadWidthFrom(stage.w, sidebarInset, storedThreadW)
  const paneInset = threadOpen ? threadW : threadTab ? THREAD_TAB_W : 0
  const chromeLeft = sidebarInset + paneInset + 16
  // The right edge is the window's own again: the reading pane left it for the
  // sidebar's side, and nothing reserves a column on the right any more.
  const chromeRight = 16
  useEffect(() => {
    useStore.getState().setViewInset(sidebarInset + paneInset, 0)
  }, [sidebarInset, paneInset])

  /* The column's box, published as a custom property so a resize drag can move
     it without going through the store — `THREAD_W_VAR` carries the whole
     argument. Written HERE from the committed width, which makes this the value
     of record: the handle runs ahead of it while a drag is live, and this puts
     it back in step the moment the drag commits, the window resizes, or a stored
     width is clamped differently on a new screen. */
  useEffect(() => {
    document.documentElement.style.setProperty(THREAD_W_VAR, `${threadW}px`)
  }, [threadW])
  /* The sidebar's inset, published the same way and for the same reason
     (`SIDEBAR_INSET_VAR`): the pane's own box and the thread column's `left`
     both read it, the sidebar's seam runs ahead of it while a drag is live,
     and this writes it back from the number of record. */
  useEffect(() => {
    document.documentElement.style.setProperty(SIDEBAR_INSET_VAR, `${sidebarInset}px`)
  }, [sidebarInset])

  /**
   * Where the composer stands. In the thread's own column whenever the column
   * is up — reading and writing about the graph are one surface, which is the
   * point of the re-homing — and floating centred over the field everywhere
   * else: the blank canvas's seed moment, a desktop with the thread closed,
   * and the phone, whose sheet-and-strip arrangement is unchanged.
   */
  const composerInPane = !narrow && railRight && nodeCount > 0
  const span = stage.w - chromeLeft - chromeRight
  /**
   * The minimap's gate, per regime. Under a floating composer it is the
   * collision math this gate has always been: the composer is a 660 column
   * centred in the span, the minimap needs 132 + 8 at the span's right edge,
   * and 12px of daylight means span/2 − 330 ≥ 152 ⇒ span ≥ 964. With the
   * composer in the pane nothing else is centred over the field, so the gate
   * is the bottom furniture's own sum — minimap 132 + 8, the old dock's 88px
   * floor, and daylight — rounded up to 280. The transport has since moved to
   * the graph's footer (D369), under the minimap, so the sum is only the
   * minimap's now and the 280 stands with room to spare. And it is the
   * reader's to put away, by the footer's Minimap switch.
   */
  const minimapOn = showMinimap && viewMode !== '3d' && (composerInPane ? span >= 280 : span >= 964)
  /**
   * The graph footer's height, measured (D369), and the line everything that
   * stood on the field's bottom edge stands on now: the composer afloat, the
   * field card and the minimap, 12 above the footer's top. Measured rather
   * than declared because the footer is not one height — a coarse pointer
   * grows its presses, and a phone's wraps onto two rows — and absent (a
   * phone at rest, a blank canvas) the edge is the old 24.
   */
  const [footH, setFootH] = useState(0)
  const footRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = footRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setFootH(Math.round(el.getBoundingClientRect().height)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const aboveFoot = footH > 0 ? footH + 12 : 24

  return (
    <div className="app">
      {/* The document's one h1. The graph is the app, so there is no visible
          title to carry it — but a screen reader still wants a top-level heading
          to anchor the page. `.sr-only` is position:absolute, so it never
          touches the single-cell grid, and it sits outside `main.stage` so a
          modal making the stage inert cannot take the page's name with it. */}
      <h1 className="sr-only">Pathways — reasoning explorer</h1>
      <main className="stage" ref={stageRef}>
        {/*
          One canvas or the other, never both. Keyed on the mode so switching
          unmounts the loop it is leaving rather than leaving two render loops and
          two ResizeObservers running over the same stage — and so each renderer's
          `screen` publication is the only one the caption layer can be reading.

          `Labels` is deliberately outside the switch. The captions are the same
          captions, placed from whichever camera just painted; giving each view its
          own copy of that 500-line declutter pass is how the two views would come
          to disagree about which claims are worth naming.
        */}
        {viewMode === '3d' ? <SpatialCanvas key="3d" /> : <NeuralCanvas key="2d" />}
        <Labels />
        {/* Beside the captions, and outside the view switch for the same reason
            they are: a pin is placed from whichever camera just painted, through
            the same `screen.place` inside the same `onAfterDraw`, so one layer
            serves both views. It sits above the captions and below every panel
            — see `--z-pins`. */}
        <CoachPins />
        {/* The card itself, beside the node its cue is about — the popup form,
            placed per frame from the same `screen.place` inside the same
            `onAfterDraw` the pins use, so one camera serves both. It mounts here
            rather than in the inspector's shell (D25): a panel about a node you
            cannot see while reading about it was the one thing the shipped layer
            got wrong. At phone width it is not here at all — `RunDoneBar` owns
            that form, as a row in the column whose edges it already measures —
            so the breakpoint is read HERE and the popup is simply not mounted
            below it, rather than mounted and returning null against a second
            copy of the same media query. */}
        {!narrow && <CoachPopup />}

        {nodeCount === 0 && <Blank insetLeft={sidebarInset} />}

        <div className="overlay">
          {/*
            The session column: a live run's thinking, in the corner the sidebar
            leaves it.

            It is still a named column, and it is still measured by four other
            files (Labels, RunDoneBar, CoachPins, Coach) — what left it was the
            exploration pill, whose jobs the sidebar took over, and then the run
            pod, whose count, move, gate and Stop became the run strip above the
            composer's input (ui/RunStrip.tsx): one place for a run's controls
            instead of a pod in this corner, chips under the input and a bar
            across the top. The move's thinking stays here rather than following
            them down, because it is reading rather than steering, and the corner
            Labels reserves for it is the one captions already give up. Its left
            edge rides `chromeLeft`, so it slides with the pane instead of being
            covered by it.

            One layout still puts the strip here: a phone with its sheet open.
            The sheet owns the bottom half and the composer yields to it, and
            with the composer went the run's one Stop. Above the sheet the strip
            would share a height with the tool dock — a Stop under the zoom
            buttons — and this corner is the one every piece of chrome already
            gives way to (the dock keeps its 62, the opener its 72). Only while a run is live or its ending
            is up: at rest there is nothing on the strip the composer will not
            say when the sheet closes.
          */}
          <div
            className="slot-session"
            style={{
              position: 'absolute',
              // Below the breakpoint the sidebar reserves no column, so this one
              // starts at the plain 16 — where the sidebar's 40×40 opener also
              // stands, in the pill's old in-flow slot. Absolutely positioned,
              // the column would paint over that opener and take its taps, and
              // during a live run the thing under the glyph is the move's
              // thinking. 16 + 40 + 16 of daylight puts it below the opener,
              // exactly as the pod stacked below the pill.
              top: narrow ? 72 : 16,
              left: chromeLeft,
              // Wider while a shared copy's line leads it, so the line and its
              // two presses sit on one row wherever the field has the room.
              width: `min(${readFirst ? 600 : 326}px, calc(100vw - ${chromeLeft + 16}px))`,
              // 62 keeps the column clear of the tool dock's vertical band — the
              // old right-bay floor, moved to the axis where it earns it.
              maxWidth: `calc(100% - ${chromeLeft + 62}px)`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 10,
              pointerEvents: 'none',
            }}
          >
            {/* A shared copy's line, first in the corner the captions and the
                run-done column already measure. Renders nothing otherwise. */}
            <SharedCopyBanner />
            <div
              className="slot-tl"
              data-stack={runLive || undefined}
              style={{ pointerEvents: 'auto' }}
            >
              {narrow && railRight && <RunStrip />}
              {runLive && <Thoughtstream />}
            </div>
          </div>

          <ToolDock stageW={stage.w} stageH={stage.h} />

          {/* The graph's footer (D369): the replay transport, the scrubber and
              the view's switches, in one strip along the field's bottom edge,
              from the column beside it to the window's edge. It replaced the
              cost spine on the field's left edge and the replay bar on its
              bottom; Labels.tsx, the coach and the camera's field all measure
              it by class. The wrapper is always mounted, so its height can be
              observed from the first commit — 0 while the footer is not up.

              A phone's yields to the thread sheet, which owns the bottom while
              it is open, the way the composer does. */}
          <div
            ref={footRef}
            style={{
              position: 'absolute',
              left: chromeLeft - 16,
              right: 0,
              bottom: 0,
              transition: 'left 380ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            {!(narrow && railRight) && (
              <GraphFooter
                stageW={stage.w}
                stageH={stage.h}
                atRest={!narrow || adjust}
                narrow={narrow}
              />
            )}
          </div>

          {/* The sheet's bottom edge, each piece floating on its own now that
              the status bar is gone, all standing on the footer: the minimap
              tucked under the tool rail at the lower right, and the composer —
              afloat — a centred 660 column. The minimap renders only when the
              VISIBLE canvas can hold it beside the composer, and never in the
              spatial view, where its viewport rectangle has no meaning and the
              plan is the floor. */}
          {/* With the composer in the pane, the field's lower-left corner holds
              the claim held, as a small card (D354, the owner's design). */}
          {composerInPane && (
            <div
              style={{
                position: 'absolute',
                left: chromeLeft + 10,
                bottom: aboveFoot,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                pointerEvents: 'none',
                transition: 'left 380ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              <FieldCard />
            </div>
          )}

          <div
            style={{
              position: 'absolute',
              left: chromeLeft,
              right: chromeRight,
              bottom: aboveFoot,
              // Over the tool dock and the footer while the dials are open:
              // on a phone the panel reaches both of their bands, and a dial
              // drawn under the zoom buttons — or under a spine row that takes
              // the tap as a seek — is a dial nobody can press. Both are back
              // the moment Adjust closes.
              zIndex: adjust ? 'calc(var(--z-panel) + 1)' : undefined,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              pointerEvents: 'none',
            }}
          >
            {/* The class carries no style. It is a NAME for this rectangle, so
                the pin layer can measure it the way Labels.tsx measures
                `.slot-session` and keep its numbered pins out from under it
                (ui/CoachPins.tsx). The column itself passes presses: the
                composer stacks its rows above the bar in the flow now, so this
                rect has transparent gaps beside and between them, and a gap
                that swallowed a click would be a dead zone over the field. Each
                piece of chrome in it opts back in — the thought stream and the
                transport by their own class, the composer's rows and bar by
                hand — and the pin layer still avoids the whole rect, which is
                the conservative side to err on. */}
            <div
              className="slot-composer"
              style={{
                width: 'min(660px, 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 10,
                pointerEvents: 'none',
              }}
            >
              {/* The live line: the model's streaming reasoning rides just above
                  the composer, per the sheet. While a RUN is going it stays in
                  the top-left stack instead — Labels reserves that corner. */}
              {/* On a phone the composer yields to the thread sheet, which
                  owns the bottom while it is open. A node tap opened the sheet;
                  dismissing it brings the composer straight back. With the
                  composer in the pane this strip renders nothing at all — its
                  zero-height rect blocks no presses and reserves no captions,
                  which both measuring layers already guard against. */}
              {!composerInPane && !(narrow && railRight) && (
                <>
                  {!runLive && <Thoughtstream />}
                  {/* What was pressed while that line was live, under it. */}
                  <UpNext />
                  {!readFirst && <Composer />}
                </>
              )}
            </div>
          </div>

          {/* The floor is the collision math, not a guessed width — derived
              per composer regime where `minimapOn` is, beside the arithmetic
              it gates on. */}
          {minimapOn && (
            <div
              style={{
                position: 'absolute',
                right: chromeRight + 8,
                bottom: aboveFoot,
                pointerEvents: 'auto',
                transition: 'right 380ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              <Minimap stageW={stage.w} stageH={stage.h} />
            </div>
          )}

          {/* The status bar retired with the Ledger redesign: the colour key
              lives in the eye popover's legend, the pace select in Settings
              (D369), and the field runs to the very edge the way the sheet's
              does. */}
        </div>

        {/* After the overlay in source order, so the pane and its shadow
            sit above the chrome that shares their layer. */}
        {/* Floating chrome of its own: the bar measures the session column
            rather than sitting in the overlay's grid, and it must stay under
            the panes' glass, so it mounts here beside them. */}
        <RunDoneBar />
        <ProgrammeSheet />
        {/* Inside the stage, beside the thread and on the same shell:
            `useModalFocus`'s one `main.stage` inert flag puts both behind every
            sheet without a second mechanism. */}
        <ExplorationsSidebar width={sidebarW} stageW={stage.w} reservedRight={threadReserve} />
        {/* Not mounted on an empty canvas: there is no thread to read before a
            seed lands, and the hero plus the centered composer own that moment. */}
        {/* The way back to a column somebody folded away. In the 13px it
            reserves for itself, hard against the sidebar's edge, so it takes
            nothing from the field it is not already holding — see `threadTab`
            above for why this one earns its place where D18's did not. */}
        {threadTab && (
          <button
            className="threadtab"
            aria-label="Open the thread"
            title="Open the thread  ⌘]"
            style={{ left: `var(${SIDEBAR_INSET_VAR}, ${sidebarInset}px)` }}
            onClick={() => setRail(true)}
          >
            <Icon name="chevron" size={13} />
          </button>
        )}
        {railRight && nodeCount > 0 && (
          <Thread left={sidebarInset} width={threadW} stageW={stage.w} />
        )}
        <CoachDirector stageW={stage.w} stageH={stage.h} />
      </main>

      <CommandPalette stageW={stage.w} stageH={stage.h} />
      <SetupSheet />
      <Library />
      <ExplorationSearch />
      {/* Export work is loaded only while open. The Lab preserves its mounted
          lifecycle after first use, while still deferring its initial download. */}
      {exportOpen && (
        <Suspense fallback={null}>
          <ExportSheet />
        </Suspense>
      )}
      {(researchOpen || researchLoaded) && (
        <Suspense
          fallback={
            researchOpen ? (
              <div className="toasts">
                <div className="toast" role="status">
                  Opening Research Lab…
                </div>
              </div>
            ) : null
          }
        >
          <ResearchLab
            onOpenReproducibilityCapsule={() => {
              const s = useStore.getState()
              s.setResearchOpen(false)
              s.setExport(true, null, 'capsule')
            }}
          />
        </Suspense>
      )}
      <ShortcutsSheet />
      <PromptSheet />
      <SourcesDialog />
      <Toasts />
    </div>
  )
}

/**
 * The empty canvas. Exported for its client-render test.
 *
 * Two readers land here, and they want different first steps (D335). Without a
 * key, the worked example IS the first step — it runs on nothing and shows what a
 * pass makes — so it keeps the big button. With one, the reader has just pressed
 * "Save and start", which cleared the canvas on purpose so that Start means the
 * question box (Setup.tsx); a big button back to the example the save flow just
 * cleared was the hero arguing with the press before it. So the keyed hero points
 * at the box, and the example drops to an ordinary button under it.
 */
export function Blank({ insetLeft }: { insetLeft: number }) {
  const setPalette = useStore((s) => s.setPalette)
  // A boolean, so the selector answers with something stable (D328).
  const keyed = useStore((s) => canCall(s))
  // A phone has no ⌘K to press, so the palette button there says what it opens.
  const narrow = useMedia('(max-width: 767px)')
  return (
    // Centred in the VISIBLE canvas — the stage runs under the floating panes,
    // and a hero centred in the full width would sit half behind their glass.
    // Only the sidebar can be open here: the hero shows on an empty canvas,
    // where the thread column is deliberately not mounted.
    <div
      className="blank"
      style={{
        paddingLeft: insetLeft ? insetLeft + 32 : undefined,
      }}
    >
      <div className="blank-inner">
        <Icon name="pathways" size={34} strokeWidth={1.25} style={{ color: 'var(--accent)' }} />
        <h2>Bring your toughest question.</h2>
        <p>
          Give Pathways an idea, a question, or a claim. It looks for other explanations,
          second-order effects, contradictions, edge cases, and dead ends, then helps you land
          somewhere new.
        </p>
        {keyed ? (
          <>
            <p>
              <strong>Type it in the box below.</strong> Enter starts the first pass.
            </p>
            <button className="btn" onClick={() => void loadDemo()}>
              <Icon name="play" size={13} />
              Or watch a worked example
            </button>
          </>
        ) : (
          <button className="btn btn--outline btn--tall" onClick={() => void loadDemo()}>
            <Icon name="play" size={13} />
            Watch a worked example
          </button>
        )}
        <button className="btn" onClick={() => setPalette(true)}>
          {narrow ? (
            'Search and commands'
          ) : (
            <>
              Or press{' '}
              <span className="kbd" style={{ margin: '0 4px' }}>
                ⌘K
              </span>{' '}
              to search
            </>
          )}
        </button>
      </div>
    </div>
  )
}
